import { useState } from "react";
import { Field } from "./Field";
import { api, type ItemDetail } from "../lib/api";
import { splitOz, toOz, totalOz } from "../lib/weights";

type Kind = "polybag" | "box";
type Dims = { length: number | null; width: number | null; height: number | null; unit: "in" };

/** A weight as two fields: pounds on the left, ounces on the right. */
function WeightFields({ label, value, onChange, disabled }: {
  label: string;
  value: { lbs: string; oz: string };
  onChange: (v: { lbs: string; oz: string }) => void;
  disabled: boolean;
}) {
  return (
    <Field label={label} hint="Pounds, ounces, or both: 35 oz, 1.3 lbs, or 1 lb 10 oz">
      <div className="weight-row weight-pair">
        <label className="unit-field">
          <input type="number" inputMode="decimal" min="0" step="0.1" aria-label={`${label} in pounds`}
            value={value.lbs} disabled={disabled} onChange={(e) => onChange({ ...value, lbs: e.target.value })} />
          <span>lbs</span>
        </label>
        <label className="unit-field">
          <input type="number" inputMode="decimal" min="0" step="0.1" aria-label={`${label} in ounces`}
            value={value.oz} disabled={disabled} onChange={(e) => onChange({ ...value, oz: e.target.value })} />
          <span>oz</span>
        </label>
      </div>
    </Field>
  );
}

/**
 * The "Packaging details" panel on an item: its weight, how it ships
 * (polybag or box) and the package's dimensions.
 */
export function PackagingPanel({ item, onClose, onSaved }: {
  item: ItemDetail;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const a = item.attributes ?? {};
  const dims = (a.packageDims ?? null) as Dims | null;
  const [itemW, setItemW] = useState(() => splitOz(toOz(a.itemWeight, a.itemWeightUnit)));
  const [kind, setKind] = useState<Kind | "">((a.packagingType as Kind | undefined) ?? "");
  const [size, setSize] = useState(() => ({
    length: dims?.length != null ? String(dims.length) : "",
    width: dims?.width != null ? String(dims.width) : "",
    height: dims?.height != null ? String(dims.height) : "",
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const num = (s: string) => (s.trim() === "" ? null : Number(s));
      const itemOz = totalOz(itemW.lbs, itemW.oz);
      const d = { length: num(size.length), width: num(size.width), height: num(size.height), unit: "in" as const };
      if ([d.length, d.width, d.height].some((v) => v !== null && (!Number.isFinite(v) || v < 0))) {
        throw new Error("Dimensions must be positive numbers");
      }
      await api.patch(`/items/${item.id}`, {
        itemWeight: itemOz, itemWeightUnit: "oz",
        packagingType: kind || null,
        packageDims: d.length === null && d.width === null && d.height === null ? null : d,
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the packaging details");
    } finally {
      setBusy(false);
    }
  }

  const dim = (key: "length" | "width" | "height", label: string) => (
    <label className="unit-field">
      <input type="number" inputMode="decimal" min="0" step="0.25" aria-label={`${label} in inches`} placeholder={label}
        value={size[key]} disabled={busy} onChange={(e) => setSize((s) => ({ ...s, [key]: e.target.value }))} />
      <span>in</span>
    </label>
  );

  return (
    <section className="rail">
      <h2 className="eyebrow rail-label">Packaging details</h2>
      <div className="row">
        <WeightFields label="Item weight" value={itemW} onChange={setItemW} disabled={busy} />
      </div>
      <div className="row">
        <Field label="Packaging type">
          <select value={kind} disabled={busy} onChange={(e) => setKind(e.target.value as Kind | "")}>
            <option value="">—</option>
            <option value="polybag">Polybag</option>
            <option value="box">Box</option>
          </select>
        </Field>
        <Field label="Package dimensions" hint="Length × width × height; height is optional for a flat polybag">
          <div className="dims-row">
            {dim("length", "Length")}
            <span aria-hidden="true">×</span>
            {dim("width", "Width")}
            <span aria-hidden="true">×</span>
            {dim("height", "Height")}
          </div>
        </Field>
      </div>
      {error && <div className="notice" style={{ marginTop: "0.75rem" }}>{error}</div>}
      <div className="actions">
        <button onClick={() => void save()} disabled={busy}>{busy ? "Saving" : "Save packaging details"}</button>
        <button className="quiet" onClick={onClose} disabled={busy}>Close</button>
      </div>
    </section>
  );
}
